import { useEffect, useRef, useMemo, startTransition } from 'react'
import { useMarketStore } from '../services/store'
import { useDashboardData } from './useDashboardData'
import { useSettingsStore } from '../stores/settingsStore'
import { extractSnapshotOptions, timeToMin, extractTimePart } from '../lib/snapshot-search'
import { IndexSymbol } from '@options-oracle/types'
import { runInWorker } from '../components/GlobalHistoryFetcher'

const FIELD_KEYS = {
    CE_OI: 'callOpenInterest',
    PE_OI: 'putOpenInterest',
    CE_VOL: 'callVolume',
    PE_VOL: 'putVolume',
    CE_OI_CHG: 'callOpenInterestChange',
    PE_OI_CHG: 'putOpenInterestChange',
}

function getFieldValue(opt: any, key: string): number {
    const val = opt[key]
    return typeof val === 'number' && !isNaN(val) ? val : 0
}

/**
 * 🚀 ZERO-FREEZE v2: useMarketWatchEngine
 * 
 * Heavy math (sorting 300+ snapshots × 23 strikes) is now offloaded to history.worker.ts.
 * This hook only handles:
 *   1. Dispatching history to the Worker when it changes
 *   2. Live tick fast-path (prepending LIVE row every 2s without re-computing)
 *   3. Re-sorting cached rows when activeSubTab changes (lightweight, <5ms)
 */
export function useMarketWatchEngine() {
    const { marketData: activeMarketData, effectiveExpiry } = useDashboardData()
    const activeSymbol = useSettingsStore(s => s.symbol)
    const storeSubTab = useSettingsStore(s => s.subTab)
    const storeStrikeRange = useSettingsStore(s => s.range)
    const isLiveMode = useSettingsStore(s => s.isLive)
    const inspectionSource = useSettingsStore(s => s.inspectionSource)

    // Select history directly from Zustand store
    const history = useMarketStore( ( state ) => {
        if ( inspectionSource === 'HistoricalSnapshot' ) return state.historicalHistory?.[ activeSymbol ]
        if ( inspectionSource === 'RangeIntraday' || inspectionSource === 'RangeEOD' ) return state.rangeHistory?.[ activeSymbol ]
        return state.intradayHistory?.[ activeSymbol ]
    } )

    const historyRef = useRef<any[]>([])
    const lastHistoryRef = useRef<any[]>([])

    useEffect(() => {
        historyRef.current = history || []
    }, [history])

    const activeSubTab = storeSubTab || 'Combined OI'
    const strikeGap = (activeSymbol === IndexSymbol.NIFTY ? 50 : 100)
    const activeRange = storeStrikeRange || 11

    // Cached base history from Worker (no live row)
    const baseHistoryCache = useRef<any[]>([])
    const baseMaxStrikes = useRef<[number, number, number, number, number]>([0,0,0,0,0])
    const lastProcessedLiveKey = useRef<string>('')
    const lastStructKey = useRef<string>('')
    const computingRef = useRef(false)
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const activeMarketDataRef = useRef(activeMarketData)
    activeMarketDataRef.current = activeMarketData

    // Derive a stable key from the live market data to detect meaningful changes
    const liveDataKey = useMemo(() => {
        if (!activeMarketData?.options?.length) return ''
        const firstOpt = activeMarketData.options[0]
        return `${activeMarketData.spotPrice}-${firstOpt?.strike}-${firstOpt?.callOpenInterest}`
    }, [activeMarketData])

    useEffect(() => {
        const h = historyRef.current
        if (!h || h.length === 0) return
        // 🛡️ IRONCLAD: Bail if no symbol — prevents Worker dispatch with empty payload
        if (!activeSymbol) return

        const historyChanged = h !== lastHistoryRef.current || baseHistoryCache.current.length === 0
        lastHistoryRef.current = h

        const parseDependencyKey = `${h.length}-${activeSymbol}-${effectiveExpiry}-${strikeGap}-${activeRange}`
        const sortDependencyKey = activeSubTab

        const lastParseKey = lastStructKey.current.split('|')[0]
        const lastSortKey = lastStructKey.current.split('|')[1]

        const parseStructChanged = lastParseKey !== parseDependencyKey || historyChanged
        const sortOnlyChanged = !parseStructChanged && lastSortKey !== sortDependencyKey

        if (parseStructChanged || sortOnlyChanged) {
            lastStructKey.current = `${parseDependencyKey}|${sortDependencyKey}`
        }

        // ─── PART 1: Full recompute via Worker (only when history/structure changes) ───
        if (parseStructChanged || baseHistoryCache.current.length === 0) {
            // 🚀 SSOT CHECK: If GlobalHistoryFetcher's COMPUTE_ALL already populated
            // marketWatchHistory, use it as our base cache instead of re-computing.
            const existingMW = useMarketStore.getState().marketWatchHistory?.[activeSymbol]
            if (existingMW && existingMW.processedHistory && existingMW.processedHistory.length === h.length) {
                // Hydrate base cache from COMPUTE_ALL results
                baseHistoryCache.current = existingMW.processedHistory.filter((r: any) => !r.isLive)
                baseMaxStrikes.current = existingMW.maxTopStrikes || [0,0,0,0,0]
                // Still prepend live row
                prependLiveRowAndDispatch()
                return
            }
            if (computingRef.current) return // Don't queue duplicate Worker calls
            if (debounceRef.current) clearTimeout(debounceRef.current)

            debounceRef.current = setTimeout(() => {
                computingRef.current = true
                console.time('[PERF] useMarketWatchEngine.Worker')

                runInWorker('COMPUTE_MARKET_WATCH_HISTORY', {
                    history: h,
                    symbol: activeSymbol,
                    expiry: effectiveExpiry || '',
                    activeSubTab,
                    strikeGap,
                    activeRange,
                }).then((result: any) => {
                    computingRef.current = false
                    console.timeEnd('[PERF] useMarketWatchEngine.Worker')
                    if (!result || !result.processedHistory) return

                    // Cache the base history (without live row)
                    baseHistoryCache.current = result.processedHistory
                    baseMaxStrikes.current = result.maxTopStrikes || [0,0,0,0,0]

                    // Prepend live row and dispatch
                    prependLiveRowAndDispatch()
                }).catch((err: any) => {
                    computingRef.current = false
                    console.error('[useMarketWatchEngine] Worker failed:', err)
                })
            }, 300)
        }
        // ─── PART 2: Sort-only change (lightweight, <5ms on main thread) ───
        else if (sortOnlyChanged) {
            const getSortKey = (r: any): number => {
                switch (activeSubTab) {
                    case 'Combined OI': return r.combOI
                    case 'Combined Volume': return r.combVol
                    case 'Top OI': return r.oi
                    case 'Top Volume': return r.volume
                    case 'Top Chng OI': return Math.abs(r.oiChg || 0)
                    case 'Vol-OI': return Math.abs(r.volOiDiff || 0)
                    case 'Vol-OI-Chng': return Math.abs(r.volOiChg || 0)
                    default: return r.oi
                }
            }

            let prevMaxStrikesArr: [number, number, number, number, number] = [0, 0, 0, 0, 0]

            for (let i = 0; i < baseHistoryCache.current.length; i++) {
                const row = baseHistoryCache.current[i]
                if (row && row.rawOpts) {
                    row.rawOpts.sort((a: any, b: any) => getSortKey(b) - getSortKey(a))
                    row.top5 = row.rawOpts.slice(0, 5)
                    row.top5.forEach((opt: any, idx: number) => {
                        if (opt && opt.strike > (prevMaxStrikesArr[idx] || 0)) {
                            prevMaxStrikesArr[idx] = opt.strike
                        }
                    })
                }
            }

            baseMaxStrikes.current = [...prevMaxStrikesArr] as [number, number, number, number, number]
            prependLiveRowAndDispatch()
        }

        // ─── PART 3: Live tick fast-path (no Worker, no heavy math) ───
        const liveKeyChanged = lastProcessedLiveKey.current !== liveDataKey
        if (liveKeyChanged && !parseStructChanged && !sortOnlyChanged && baseHistoryCache.current.length > 0) {
            lastProcessedLiveKey.current = liveDataKey
            prependLiveRowAndDispatch()
        }

        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current)
        }
    }, [history, liveDataKey, activeSymbol, effectiveExpiry, activeSubTab, strikeGap, activeRange, inspectionSource])

    // ─── Live Row Prepender (runs on main thread, but only processes 1 snapshot) ───
    const prependLiveRowAndDispatch = () => {
        const baseRows = baseHistoryCache.current || []
        const finalRows = [...baseRows]
        const maxStrikesArr = [...(baseMaxStrikes.current || [0,0,0,0,0])] as [number, number, number, number, number]
        let maxChanged: [boolean, boolean, boolean, boolean, boolean] = [false, false, false, false, false]

        let overallMaxLTP = 0
        let overallMinLTP = Infinity

        for (const r of finalRows) {
            if (r.spotPrice > 0) {
                if (r.spotPrice > overallMaxLTP) overallMaxLTP = r.spotPrice
                if (r.spotPrice < overallMinLTP) overallMinLTP = r.spotPrice
            }
        }

        if (activeMarketDataRef.current && activeMarketDataRef.current.options && activeMarketDataRef.current.options.length > 0) {
            const liveRow = processLiveSnapshot(activeMarketDataRef.current, activeSymbol, effectiveExpiry, strikeGap, activeRange, activeSubTab)
            if (liveRow) {
                finalRows.push(liveRow) // FIX: Maintain chronological order (oldest to newest)
                liveRow.top5.forEach((opt: any, idx: number) => {
                    if (opt && opt.strike > (maxStrikesArr[idx as 0|1|2|3|4] || 0)) {
                        maxStrikesArr[idx as 0|1|2|3|4] = opt.strike
                        maxChanged[idx as 0|1|2|3|4] = true
                    }
                })
                if (liveRow.spotPrice > 0) {
                    if (liveRow.spotPrice > overallMaxLTP) overallMaxLTP = liveRow.spotPrice
                    if (liveRow.spotPrice < overallMinLTP) overallMinLTP = liveRow.spotPrice
                }
            }
        }

        startTransition(() => {
            useMarketStore.getState().setMarketWatchHistory(activeSymbol, {
                processedHistory: finalRows,
                maxIndexLTP: overallMaxLTP,
                minIndexLTP: overallMinLTP === Infinity ? 0 : overallMinLTP,
                maxTopStrikes: maxStrikesArr,
                maxChanged
            })
        })
    }
}

// ─── Process a single live snapshot (fast, ~1ms) ───
function processLiveSnapshot(
    snap: any, activeSymbol: string, effectiveExpiry: string,
    strikeGap: number, activeRange: number, activeSubTab: string
) {
    const spotPrice = snap.spotPrice || snap.indexLtp || 0
    let atmStrike = snap.atmStrike || 0
    if (atmStrike <= 0 && spotPrice > 0) {
        atmStrike = Math.round(spotPrice / strikeGap) * strikeGap
    }

    const opts = snap.options || []
    if (!opts || opts.length === 0) return null

    if (atmStrike <= 0 && opts.length > 0) {
        atmStrike = opts[Math.floor(opts.length / 2)]?.strike || 0
    }

    const minSt = atmStrike - activeRange * strikeGap
    const maxSt = atmStrike + activeRange * strikeGap

    const filteredOpts = opts.filter((opt: any) => {
        const strike = opt.strike ?? opt.strikePrice ?? 0
        return strike >= minSt && strike <= maxSt
    })

    const getSortKey = (r: any): number => {
        switch (activeSubTab) {
            case 'Combined OI': return r.combOI
            case 'Combined Volume': return r.combVol
            case 'Top OI': return r.oi
            case 'Top Volume': return r.volume
            case 'Top Chng OI': return Math.abs(r.oiChg || 0)
            case 'Vol-OI': return Math.abs(r.volOiDiff || 0)
            case 'Vol-OI-Chng': return Math.abs(r.volOiChg || 0)
            default: return r.oi
        }
    }

    const snapRows: any[] = []
    for (const opt of filteredOpts) {
        const s = opt.strike ?? opt.strikePrice ?? 0
        const ceOI = getFieldValue(opt, FIELD_KEYS.CE_OI)
        const peOI = getFieldValue(opt, FIELD_KEYS.PE_OI)
        const ceVol = getFieldValue(opt, FIELD_KEYS.CE_VOL)
        const peVol = getFieldValue(opt, FIELD_KEYS.PE_VOL)
        const ceOiChg = getFieldValue(opt, FIELD_KEYS.CE_OI_CHG)
        const peOiChg = getFieldValue(opt, FIELD_KEYS.PE_OI_CHG)

        const ceVolOiDiff = ceVol - ceOI
        const peVolOiDiff = peVol - peOI
        const ceVolOiChg = (ceOiChg || 0) > 0 ? ceVolOiDiff - (ceOiChg || 0) : ceVolOiDiff + (ceOiChg || 0)
        const peVolOiChg = (peOiChg || 0) > 0 ? peVolOiDiff - (peOiChg || 0) : peVolOiDiff + (peOiChg || 0)

        snapRows.push({
            strike: s, type: 'CE',
            combOI: ceOI + peOI, combVol: ceVol + peVol,
            oi: ceOI, volume: ceVol, ceVol, peVol, oiChg: ceOiChg || 0,
            volOiDiff: ceVolOiDiff, volOiChg: ceVolOiChg,
        })
        snapRows.push({
            strike: s, type: 'PE',
            combOI: ceOI + peOI, combVol: ceVol + peVol,
            oi: peOI, volume: peVol, ceVol, peVol, oiChg: peOiChg || 0,
            volOiDiff: peVolOiDiff, volOiChg: peVolOiChg,
        })
    }

    snapRows.sort((a, b) => getSortKey(b) - getSortKey(a))
    const top5 = snapRows.slice(0, 5)

    return {
        time: 'Live',
        fullDate: '',
        timeVal: 9999,
        originalSortValue: 'ZZZ_LIVE',
        spotPrice,
        top5,
        rawOpts: snapRows,
        isLive: true
    }
}
